import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

/**
 * Página "Em Breve" para usuários pendentes de aprovação
 * Exibida quando account_status !== 'approved'
 */
const PendingApprovalPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('pending');

  useEffect(() => {
    // Tenta pegar email do localStorage
    try {
      const pendingUser = localStorage.getItem('arena_stats_pending_user');
      const user = localStorage.getItem('arena_stats_user');
      
      if (pendingUser) {
        const data = JSON.parse(pendingUser);
        setEmail(data.email || '');
        setStatus(data.account_status || 'pending');
      } else if (user) {
        const data = JSON.parse(user);
        setEmail(data.email || '');
      }
    } catch (e) {
      console.error('[PendingApproval] Erro ao carregar dados:', e);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('arena_stats_user');
    localStorage.removeItem('arena_stats_pending_user');
    navigate('/login');
  };

  const getStatusConfig = () => {
    switch (status) {
      case 'pending':
        return {
          icon: <Clock className="w-20 h-20 text-yellow-500" />,
          title: 'Aguardando Lançamento',
          message: 'Seu acesso ainda não está liberado.',
          subtitle: 'Em breve você poderá acessar a plataforma.',
          color: 'yellow',
          borderColor: '#eab308',
          bgColor: '#fefce8'
        };
      case 'rejected':
        return {
          icon: <XCircle className="w-20 h-20 text-red-500" />,
          title: 'Cadastro Negado',
          message: 'Infelizmente seu cadastro não foi aprovado.',
          subtitle: 'Entre em contato conosco para mais informações.',
          color: 'red',
          borderColor: '#ef4444',
          bgColor: '#fef2f2'
        };
      case 'suspended':
        return {
          icon: <AlertTriangle className="w-20 h-20 text-orange-500" />,
          title: 'Conta Suspensa',
          message: 'Sua conta foi temporariamente suspensa.',
          subtitle: 'Entre em contato com o suporte para resolver esta situação.',
          color: 'orange',
          borderColor: '#f97316',
          bgColor: '#fff7ed'
        };
      default:
        return {
          icon: <Clock className="w-20 h-20 text-yellow-500" />,
          title: 'Aguardando Lançamento',
          message: 'Seu acesso ainda não está liberado.',
          subtitle: 'Em breve você poderá acessar a plataforma.',
          color: 'yellow',
          borderColor: '#eab308',
          bgColor: '#fefce8'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
      <div className="max-w-2xl w-full mx-4">
        {/* Card principal */}
        <div 
          className="bg-white rounded-2xl shadow-xl border-2 p-8 lg:p-12"
          style={{ borderColor: config.borderColor }}
        >
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 rounded-2xl border-4" style={{ borderColor: '#b48c2c', overflow: 'hidden' }}>
              <img 
                src="/vukz-logo.jpeg" 
                alt="Arena Stats" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Ícone de status */}
          <div className="flex justify-center mb-6">
            {config.icon}
          </div>

          {/* Título */}
          <h1 
            className="text-4xl font-extrabold text-center mb-4"
            style={{ color: '#111111' }}
          >
            {config.title}
          </h1>

          {/* Mensagem principal */}
          <p className="text-xl text-gray-700 text-center mb-3">
            {config.message}
          </p>

          {/* Subtítulo */}
          <p className="text-gray-500 text-center mb-8">
            {config.subtitle}
          </p>

          {/* Email do usuário */}
          {email && (
            <div 
              className="rounded-lg p-4 mb-6"
              style={{ backgroundColor: config.bgColor }}
            >
              <p className="text-sm text-gray-600 text-center">
                Cadastro realizado com o e-mail:
              </p>
              <p className="text-lg font-bold text-center mt-1" style={{ color: '#111111' }}>
                {email}
              </p>
            </div>
          )}

          {/* Informações adicionais */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              O que acontece agora?
            </h2>
            <ul className="space-y-2 text-gray-600 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Nossa equipe analisa todas as solicitações manualmente</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Você receberá um e-mail assim que seu acesso for liberado</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>O processo costuma levar de 24 a 48 horas</span>
              </li>
            </ul>
          </div>

          {/* Ações */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleLogout}
              className="flex-1 py-3 px-6 rounded-lg font-bold text-white hover:opacity-90 transition-all duration-200"
              style={{ backgroundColor: '#111111' }}
            >
              Voltar ao Login
            </button>
            
            {status === 'rejected' && (
              <button
                onClick={() => window.location.href = 'mailto:contato@vukz.ai'}
                className="flex-1 py-3 px-6 rounded-lg font-bold border-2 transition-all duration-200"
                style={{ 
                  borderColor: '#b48c2c', 
                  color: '#111111',
                  backgroundColor: 'white'
                }}
              >
                Falar com Suporte
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Precisa de ajuda?{' '}
          <a 
            href="mailto:contato@vukz.ai" 
            className="font-bold hover:underline"
            style={{ color: '#b48c2c' }}
          >
            contato@vukz.ai
          </a>
        </p>
      </div>
    </div>
  );
};

export default PendingApprovalPage;
